import React from 'react';
import {
    FORGE_REQUEST_CREATE,
    PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
    TOP_LEVEL_COMPONENTS,
} from 'view/common/forge-ui/constants';
import { ExtensionPointProvider } from 'view/common/forge-ui/extension-point-provider';
import { ExtensionProvider } from 'view/common/forge-ui/extension-provider';
import { IframeRenderer } from 'view/common/forge-ui/iframe-renderer';
import { Renderer } from 'view/common/forge-ui/renderer';
import { requestCreatePropertyPanelUIKitComponents } from 'view/common/forge-ui-components';
import { isUIKit2Enabled } from 'feature-flags';

import type {
    Extension,
    ExtensionData,
    ForgeRequestCreatePropertyFormData,
    ForgeAppRequestCreatePropertyData,
    WithExtensionListProps,
    ForgeDoc,
    ForgeDocProps,
    ForgeRequestCreatePropertyFields,
    WithBlockedAppsBannerProps,
} from 'view/common/forge-ui/types';
import { withExtensionList } from 'view/common/forge-ui/with-extension-list';
import { map } from 'lodash';

// @ts-ignore TS(2305) TypeScript upgrade 5.1.6, please fix this violation when you revisit this code.: Module '"@atlassian/forge-ui/dist/types/renderer/R... Remove this comment to see the full error message
import type { rendererNextComponents } from '@atlassian/forge-ui/dist/types/renderer/RendererNext';
import { TopLevelComponent } from 'view/common/forge-ui/top-level-component';

import { isCustomUIExtension } from 'util/forge-ui';
import { useShowBanner } from 'util/blocked-apps-banner';

interface RequestCreatePropertyPanelModuleProps extends WithExtensionListProps {
    portalId: number;
    requestTypeId: number;
    onUpdateRequestPropertyData: (data: ForgeRequestCreatePropertyFormData) => void;
}

export interface ForgeRequestCreateProps {
    extension: Extension;
    extensionData: ExtensionData;
    onUpdate: (data: ForgeRequestCreatePropertyFormData) => void;
}

export const ForgeRequestCreatePropertyReactFragment: React.FC<ForgeRequestCreateProps> = ({
    extension,
    extensionData,
    onUpdate,
}) => {
    const submit = (payload: ForgeAppRequestCreatePropertyData) => {
        const extId = extension.id.split('/');
        onUpdate({ data: payload.data, extensionId: extId[1] });
        return true;
    };

    const submitUIKit = React.useCallback(
        (forgeDoc: ForgeDoc) => {
            const extId = extension.id.split('/');
            const forgeDocProps: ForgeDocProps = forgeDoc?.children[0].props as unknown as ForgeDocProps;
            if (forgeDocProps?.fieldValue?.updated) {
                const val = forgeDocProps.fieldValue.value?.data;
                const result = map(val, (value, key) => ({ key, value })) as ForgeRequestCreatePropertyFields[];
                onUpdate({
                    data: { fields: result, isValid: forgeDocProps.fieldValue.value?.isValid || false },
                    extensionId: extId[1],
                });
            }
        },
        [extension.id, onUpdate]
    );

    const loadRenderer = () => {
        if (isUIKit2Enabled()) {
            return (
                <Renderer
                    extension={extension}
                    extensionData={extensionData}
                    getComponents={getComponents}
                    bridge={{ submit }}
                />
            );
        }
        return (
            <Renderer
                extension={extension}
                extensionData={extensionData}
                getComponents={getComponents}
                onSubmit={submitUIKit}
            />
        );
    };

    const getComponents = React.useCallback(
        (defaults: typeof rendererNextComponents) => ({
            ...defaults,
            ...requestCreatePropertyPanelUIKitComponents,
            [TOP_LEVEL_COMPONENTS[PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE]]: TopLevelComponent,
        }),
        []
    );

    return (
        <React.Fragment key={extension.id}>
            <ExtensionProvider extension={extension}>
                {isCustomUIExtension(extension) ? (
                    <IframeRenderer
                        extension={extension}
                        module={PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE}
                        extensionData={extensionData}
                        bridge={{ submit }}
                    />
                ) : (
                    loadRenderer()
                )}
            </ExtensionProvider>
        </React.Fragment>
    );
};

export const RequestCreatePropertyPanelModuleDumb: React.FC<
    RequestCreatePropertyPanelModuleProps & WithBlockedAppsBannerProps
> = ({ extensions, portalId, requestTypeId, onUpdateRequestPropertyData, showBanner, resetBannerState }) => {
    const extensionData: ExtensionData = React.useMemo(
        () => ({
            type: PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE,
            portal: { id: portalId },
            request: { typeId: requestTypeId },
        }),
        [portalId, requestTypeId]
    );

    const allowedExtensions = useShowBanner(extensions, FORGE_REQUEST_CREATE, true, showBanner, resetBannerState);

    return (
        <>
            {allowedExtensions.length > 0 && (
                <ExtensionPointProvider extensionType={PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE}>
                    {allowedExtensions.map(
                        (extension: Extension) =>
                            extension && (
                                <ForgeRequestCreatePropertyReactFragment
                                    extension={extension}
                                    extensionData={extensionData}
                                    onUpdate={onUpdateRequestPropertyData}
                                    key={extension.id}
                                />
                            )
                    )}
                </ExtensionPointProvider>
            )}
        </>
    );
};

export const RequestCreatePropertyPanel = withExtensionList(PORTAL_REQUEST_CREATE_PROPERTY_PANEL_MODULE)(
    RequestCreatePropertyPanelModuleDumb
);

export default RequestCreatePropertyPanel;
